import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, TextField, MenuItem, Grid, Alert,
  CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, Card, CardContent, Tooltip, LinearProgress, Collapse,
  Accordion, AccordionSummary, AccordionDetails, Divider, FormControlLabel, Switch
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon, Delete as DeleteIcon,
  LocalShipping as ShipIcon, KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon, CheckCircle as CheckIcon, 
  Warning as WarningIcon, ExpandMore as ExpandMoreIcon, 
  TrendingUp as TrendingIcon, Inventory as InventoryIcon,
  Info as InfoIcon, Edit as EditIcon, ContentCopy as CopyIcon,
  PlaylistAdd as BulkAddIcon, RemoveCircle as RemoveIcon,
  FolderOpen as FolderIcon
} from '@mui/icons-material';
import axios from 'axios';
import { loadDropdowns, getDropdownOptions } from '../../utils/NAV_ID_MAPPING';

const API_BASE_URL = 'https://ccc4cs.choicegroup.in/api';

const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true };
};

const generateFclMonthOptions = () => {
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const options = [];
  const today = new Date();
  for (let i = 3; i >= -12; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    options.push(`${months[d.getMonth()]}-${d.getFullYear()}`);
  }
  return options;
};

const FCL_MONTHS = generateFclMonthOptions();

const STATUS_COLORS = {
  'PENDING': { bg: '#fff3e0', color: '#e65100' },
  'PARTIAL': { bg: '#e3f2fd', color: '#1565c0' },
  'COMPLETED': { bg: '#e8f5e9', color: '#2e7d32' },
  'CANCELLED': { bg: '#ffebee', color: '#c62828' }
};

const initialFormData = {
  fcl_month: '', fcl_no: '', po_number: '', destination: '', notes: ''
};

const initialProductRow = {
  product: '', grade: '', pack_size: '', brand: '', trace_id: '', day_code: '', planned_cartons: ''
};

function ShipmentPlanningDashboard({ user }) {
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [filterFclMonth, setFilterFclMonth] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedFcl, setExpandedFcl] = useState({});
  const [matchData, setMatchData] = useState({});
  const [matchLoading, setMatchLoading] = useState({});
  const [potentialData, setPotentialData] = useState({});
  
  const [useExistingFcl, setUseExistingFcl] = useState(false);
  const [existingFclList, setExistingFclList] = useState([]);
  const [selectedExistingFcl, setSelectedExistingFcl] = useState(null);
  
  const [filterOptions, setFilterOptions] = useState({
    products: [], grades: [], pack_sizes: [], brands: []
  });
  
  const [fclData, setFclData] = useState(initialFormData);
  const [productRows, setProductRows] = useState([{ ...initialProductRow }]);
  const [editFormData, setEditFormData] = useState({ ...initialFormData, ...initialProductRow, status: 'PENDING' });
  
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const [summary, setSummary] = useState({
    total: 0, pending: 0, partial: 0, completed: 0, totalCartons: 0, totalLbs: 0
  });

  const fetchRequirements = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ action: 'list' });
      if (filterFclMonth) params.append('fcl_month', filterFclMonth);
      if (filterStatus) params.append('status', filterStatus);

      const response = await axios.get(`${API_BASE_URL}/shipment_planning.php?${params.toString()}`, getAuthConfig());

      if (response.data.success) {
        const data = response.data.data || [];
        setRequirements(data);
        setSummary({
          total: data.length,
          pending: data.filter(r => r.status === 'PENDING').length,
          partial: data.filter(r => r.status === 'PARTIAL').length,
          completed: data.filter(r => r.status === 'COMPLETED').length,
          totalCartons: data.reduce((sum, r) => sum + parseInt(r.planned_cartons || 0), 0),
          totalLbs: data.reduce((sum, r) => sum + parseFloat(r.planned_lbs || 0), 0)
        });
        
        // Auto-expand all FCLs initially
        const fclGroups = {};
        data.forEach(row => {
          const key = `${row.fcl_month}-${row.fcl_no}`;
          fclGroups[key] = true;
        });
        setExpandedFcl(fclGroups);
        
        // Load potential data for items with balance
        data.forEach(row => {
          if (row.balance_cartons > 0 && !potentialData[row.id]) {
            loadPotentialData(row.id);
          }
        });
        
        setError('');
      } else {
        setError(response.data.error || 'Failed to load');
      }
    } catch (err) {
      setError('Failed to load: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [filterFclMonth, filterStatus]);

  const loadPotentialData = async (id) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/shipment_planning.php?action=match&id=${id}`, getAuthConfig());
      if (response.data.success) {
        setPotentialData(prev => ({ ...prev, [id]: response.data.data }));
      }
    } catch (err) {
      console.error('Failed to load potential data:', err);
    }
  };

  const fetchFilterOptions = useCallback(async () => {
    try {
      await loadDropdowns();
      const cachedOptions = getDropdownOptions();
      setFilterOptions({
        products: cachedOptions.products || [],
        grades: cachedOptions.grades || [],
        pack_sizes: cachedOptions.pack_sizes || [],
        brands: cachedOptions.brands || []
      });
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  }, []);

  const fetchExistingFclList = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/shipment_planning.php?action=fcl_list`, getAuthConfig());
      if (response.data.success) {
        setExistingFclList(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to load FCL list:', err);
    }
  }, []);

  useEffect(() => {
    fetchRequirements();
    fetchFilterOptions();
    fetchExistingFclList();
  }, [fetchRequirements, fetchFilterOptions, fetchExistingFclList]);

  // Group requirements by FCL
  const groupedRequirements = React.useMemo(() => {
    const groups = {};
    requirements.forEach(req => {
      const key = `${req.fcl_month}-${req.fcl_no}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          fcl_month: req.fcl_month,
          fcl_no: req.fcl_no,
          po_number: req.po_number,
          destination: req.destination,
          items: [],
          totalPlanned: 0,
          totalAvailable: 0,
          totalBalance: 0,
          totalLbs: 0
        };
      }
      groups[key].items.push(req);
      groups[key].totalPlanned += parseInt(req.planned_cartons || 0);
      groups[key].totalAvailable += parseInt(req.available_cartons || 0);
      groups[key].totalBalance += parseInt(req.balance_cartons || 0);
      groups[key].totalLbs += parseFloat(req.planned_lbs || 0);
    });
    return Object.values(groups).sort((a, b) => {
      if (a.fcl_month !== b.fcl_month) return b.fcl_month.localeCompare(a.fcl_month);
      return parseInt(b.fcl_no) - parseInt(a.fcl_no);
    });
  }, [requirements]);

  const toggleFclExpansion = (key) => {
    setExpandedFcl(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleRowExpansion = async (row) => {
    if (expandedRow === row.id) {
      setExpandedRow(null);
      return;
    }
    setExpandedRow(row.id);
    if (!matchData[row.id]) {
      setMatchLoading(prev => ({ ...prev, [row.id]: true }));
      try {
        const response = await axios.get(`${API_BASE_URL}/shipment_planning.php?action=match&id=${row.id}`, getAuthConfig());
        if (response.data.success) {
          setMatchData(prev => ({ ...prev, [row.id]: response.data.data }));
        }
      } catch (err) {
        console.error('Failed to load match data:', err);
      } finally {
        setMatchLoading(prev => ({ ...prev, [row.id]: false }));
      }
    }
  };

  const handleEdit = (row) => {
    setEditFormData({
      fcl_month: row.fcl_month || '', fcl_no: row.fcl_no || '', po_number: row.po_number || '',
      product: row.product || '', grade: row.grade || '', pack_size: row.pack_size || '',
      brand: row.brand || '', trace_id: row.trace_id || '', day_code: row.day_code || '',
      planned_cartons: row.planned_cartons || '', destination: row.destination || '',
      notes: row.notes || '', status: row.status || 'PENDING'
    });
    setEditingId(row.id);
    setShowEditModal(true);
  };

  const handleAddMoreToFcl = (fcl) => {
    setFclData({
      fcl_month: fcl.fcl_month, fcl_no: fcl.fcl_no,
      po_number: fcl.po_number || '', destination: fcl.destination || '', notes: ''
    });
    setProductRows([{ ...initialProductRow }]);
    setUseExistingFcl(false);
    setShowAddModal(true);
  };

  const addProductRow = () => setProductRows([...productRows, { ...initialProductRow }]);
  
  const removeProductRow = (index) => {
    if (productRows.length > 1) setProductRows(productRows.filter((_, i) => i !== index));
  };

  const updateProductRow = (index, field, value) => {
    const newRows = [...productRows];
    newRows[index] = { ...newRows[index], [field]: value };
    setProductRows(newRows);
  };

  const duplicateProductRow = (index) => {
    const row = productRows[index];
    const newRow = { ...row, trace_id: '', day_code: '', planned_cartons: '' };
    const newRows = [...productRows];
    newRows.splice(index + 1, 0, newRow);
    setProductRows(newRows);
  };

  const handleSubmitAll = async () => {
    setFormLoading(true);
    setFormError('');
    const validRows = productRows.filter(row => 
      row.product && row.grade && row.pack_size && row.brand && 
      row.trace_id && row.day_code && row.planned_cartons
    );
    if (validRows.length === 0) {
      setFormError('Please fill in at least one complete product row');
      setFormLoading(false);
      return;
    }
    try {
      let successCount = 0, errorCount = 0;
      for (const row of validRows) {
        try {
          const response = await axios.post(`${API_BASE_URL}/shipment_planning.php`, { ...fclData, ...row }, getAuthConfig());
          if (response.data.success) successCount++; else errorCount++;
        } catch { errorCount++; }
      }
      if (successCount > 0) {
        setSuccess(`Added ${successCount} requirement(s)${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
        setShowAddModal(false);
        resetForm();
        fetchRequirements();
        fetchExistingFclList();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setFormError('Failed to add requirements');
      }
    } catch { setFormError('Failed to add requirements'); }
    finally { setFormLoading(false); }
  };

  const handleUpdate = async () => {
    setFormLoading(true);
    setFormError('');
    try {
      const response = await axios.put(`${API_BASE_URL}/shipment_planning.php`, { ...editFormData, id: editingId }, getAuthConfig());
      if (response.data.success) {
        setSuccess('Updated successfully!');
        setShowEditModal(false);
        setEditingId(null);
        setMatchData(prev => { const n = { ...prev }; delete n[editingId]; return n; });
        setPotentialData(prev => { const n = { ...prev }; delete n[editingId]; return n; });
        fetchRequirements();
        setTimeout(() => setSuccess(''), 3000);
      } else { setFormError(response.data.error || 'Failed to update'); }
    } catch (err) { setFormError(err.response?.data?.error || 'Failed to update'); }
    finally { setFormLoading(false); }
  };

  const resetForm = () => {
    setFclData(initialFormData);
    setProductRows([{ ...initialProductRow }]);
    setFormError('');
    setUseExistingFcl(false);
    setSelectedExistingFcl(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this requirement?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/shipment_planning.php?id=${id}`, getAuthConfig());
      setSuccess('Deleted');
      fetchRequirements();
      fetchExistingFclList();
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Failed to delete'); }
  };

  const handleExistingFclSelect = (fcl) => {
    if (fcl) {
      setFclData(prev => ({ ...prev, fcl_month: fcl.fcl_month, fcl_no: fcl.fcl_no, po_number: fcl.po_number || '', destination: fcl.destination || '' }));
      setSelectedExistingFcl(fcl);
    }
  };

  const calculateLbs = (packSize, cartons) => {
    if (!packSize || !cartons) return { lbs: 0, kgs: 0 };
    const match = packSize.match(/(\d+\.?\d*)\s*[\/xX]\s*(\d+\.?\d*)\s*(LB|LBS|KG|KGS|OZ)?/i);
    if (match) {
      const count = parseFloat(match[1]), weight = parseFloat(match[2]);
      const unit = (match[3] || 'LB').toUpperCase();
      const wpc = count * weight;
      let lbs = 0, kgs = 0;
      if (unit.includes('OZ')) { lbs = (wpc / 16) * cartons; kgs = lbs * 0.4536; }
      else if (unit.includes('KG')) { kgs = wpc * cartons; lbs = kgs * 2.205; }
      else { lbs = wpc * cartons; kgs = lbs * 0.4536; }
      return { lbs: Math.round(lbs), kgs: Math.round(kgs) };
    }
    return { lbs: 0, kgs: 0 };
  };

  const getPotentialMessage = (row) => {
    const data = potentialData[row.id];
    if (!data) return null;
    const planned = parseInt(row.planned_cartons) || 0;
    const exact = data.totals?.exact_match_cartons || 0;
    const excess = data.totals?.fcl_excess_exact_cartons || 0;
    const repack = data.totals?.fcl_excess_repack_cartons || 0;
    const total = exact + excess + repack;
    if (exact >= planned) return { type: 'success', text: `✅ 100% from Exact Match (${exact} cartons)` };
    if (total >= planned) return { type: 'info', text: `📦 100% possible: Exact(${exact}) + Excess(${excess}) + Repack(${repack}) = ${total}` };
    if (total > 0) { const pct = Math.round((total / planned) * 100); return { type: 'warning', text: `⚠️ ${pct}% (${total}/${planned}): Exact(${exact}) + Excess(${excess}) + Repack(${repack})` }; }
    return { type: 'error', text: `❌ No inventory. Need ${planned} cartons.` };
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f0f2f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShipIcon sx={{ fontSize: 36, color: '#1565c0' }} /> Shipment Planning
          </Typography>
          <Typography variant="body2" color="text.secondary">FCL Planning & Inventory Matching</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchRequirements} disabled={loading}>Refresh</Button>
          <Button variant="contained" startIcon={<BulkAddIcon />} onClick={() => { resetForm(); setShowAddModal(true); }}
            sx={{ background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', fontWeight: 600 }}>
            Add Requirements
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Lines', value: summary.total, gradient: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)' },
          { label: 'Pending', value: summary.pending, gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' },
          { label: 'Partial', value: summary.partial, gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' },
          { label: 'Completed', value: summary.completed, gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
          { label: 'Total Cartons', value: summary.totalCartons.toLocaleString(), gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' },
          { label: 'Total LBs', value: Math.round(summary.totalLbs).toLocaleString(), gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)' },
        ].map((item, idx) => (
          <Grid item xs={6} sm={4} md={2} key={idx}>
            <Card sx={{ background: item.gradient, color: 'white' }}>
              <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="h4" fontWeight={700}>{item.value}</Typography>
                <Typography variant="body2">{item.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters - MUCH WIDER */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>🔍 FILTER REQUIREMENTS</Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField 
            select 
            label="FCL Month" 
            value={filterFclMonth} 
            onChange={(e) => setFilterFclMonth(e.target.value)}
            sx={{ minWidth: 350, flex: 1 }}
            InputProps={{ sx: { fontSize: '1.1rem', height: 56 } }}
            InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
          >
            <MenuItem value="">All Months</MenuItem>
            {FCL_MONTHS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
          <TextField 
            select 
            label="Status" 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            sx={{ minWidth: 350, flex: 1 }}
            InputProps={{ sx: { fontSize: '1.1rem', height: 56 } }}
            InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="PARTIAL">Partial</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
          </TextField>
          <Button variant="outlined" onClick={() => { setFilterFclMonth(''); setFilterStatus(''); }} sx={{ height: 56, minWidth: 180, fontSize: '1rem', fontWeight: 600 }}>
            CLEAR FILTERS
          </Button>
        </Box>
      </Paper>

      {/* FCL Groups */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : groupedRequirements.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No requirements found. Click "Add Requirements" to create.</Typography>
        </Paper>
      ) : (
        groupedRequirements.map((fcl) => (
          <Paper key={fcl.key} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', border: '2px solid #1565c0' }}>
            {/* FCL Header */}
            <Box 
              sx={{ 
                background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', 
                color: 'white', 
                p: 2, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleFclExpansion(fcl.key)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton size="small" sx={{ color: 'white' }}>
                  {expandedFcl[fcl.key] ? <ExpandIcon /> : <CollapseIcon sx={{ transform: 'rotate(-90deg)' }} />}
                </IconButton>
                <ShipIcon sx={{ fontSize: 32 }} />
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    FCL: {fcl.fcl_month} #{fcl.fcl_no}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {fcl.po_number && `PO: ${fcl.po_number} | `}{fcl.destination && `${fcl.destination} | `}{fcl.items.length} Product Lines
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={700}>{fcl.totalPlanned.toLocaleString()}</Typography>
                  <Typography variant="caption">Planned</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={700} sx={{ color: fcl.totalAvailable > 0 ? '#a5d6a7' : '#ffcdd2' }}>{fcl.totalAvailable.toLocaleString()}</Typography>
                  <Typography variant="caption">Available</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={700} sx={{ color: fcl.totalBalance > 0 ? '#ffcdd2' : '#a5d6a7' }}>{fcl.totalBalance.toLocaleString()}</Typography>
                  <Typography variant="caption">Balance</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                  <Typography variant="h5" fontWeight={700}>{Math.round(fcl.totalLbs).toLocaleString()}</Typography>
                  <Typography variant="caption">LBs</Typography>
                </Box>
                <Tooltip title="Add more products to this FCL">
                  <IconButton sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }} onClick={(e) => { e.stopPropagation(); handleAddMoreToFcl(fcl); }}>
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* FCL Products */}
            <Collapse in={expandedFcl[fcl.key]}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#e3f2fd', fontWeight: 700, width: 50 }}></TableCell>
                      {['Product', 'Grade', 'Pack Size', 'Brand', 'Trace ID', 'Day Code', 'Planned', 'Available', 'Balance', 'Progress', 'Status', 'Actions'].map(h => (
                        <TableCell key={h} sx={{ bgcolor: '#e3f2fd', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fcl.items.map((row) => {
                      const potentialMsg = getPotentialMessage(row);
                      return (
                        <React.Fragment key={row.id}>
                          <TableRow hover sx={{ cursor: 'pointer', bgcolor: expandedRow === row.id ? '#f0f7ff' : 'inherit' }}>
                            <TableCell>
                              <IconButton size="small" onClick={() => toggleRowExpansion(row)} color="primary">
                                {expandedRow === row.id ? <CollapseIcon /> : <ExpandIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell onClick={() => toggleRowExpansion(row)} sx={{ fontWeight: 700 }}>{row.product}</TableCell>
                            <TableCell onClick={() => toggleRowExpansion(row)}>{row.grade}</TableCell>
                            <TableCell onClick={() => toggleRowExpansion(row)}>{row.pack_size}</TableCell>
                            <TableCell onClick={() => toggleRowExpansion(row)} sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.brand}</TableCell>
                            <TableCell onClick={() => toggleRowExpansion(row)} sx={{ fontWeight: 600, color: '#1565c0' }}>{row.trace_id}</TableCell>
                            <TableCell onClick={() => toggleRowExpansion(row)}>{row.day_code}</TableCell>
                            <TableCell align="center" onClick={() => toggleRowExpansion(row)}>
                              <Typography fontWeight={700} color="primary">{row.planned_cartons}</Typography>
                            </TableCell>
                            <TableCell align="center" onClick={() => toggleRowExpansion(row)}>
                              <Typography fontWeight={700} sx={{ color: row.available_cartons > 0 ? '#2e7d32' : '#d32f2f' }}>{row.available_cartons || 0}</Typography>
                            </TableCell>
                            <TableCell align="center" onClick={() => toggleRowExpansion(row)}>
                              <Typography fontWeight={700} sx={{ color: row.balance_cartons > 0 ? '#d32f2f' : '#2e7d32' }}>{row.balance_cartons || 0}</Typography>
                            </TableCell>
                            <TableCell sx={{ minWidth: 100 }} onClick={() => toggleRowExpansion(row)}>
                              <LinearProgress variant="determinate" value={row.fulfillment_percent || 0} sx={{ height: 8, borderRadius: 4, mb: 0.5 }} />
                              <Typography variant="body2" fontWeight={600}>{row.fulfillment_percent || 0}%</Typography>
                            </TableCell>
                            <TableCell onClick={() => toggleRowExpansion(row)}>
                              <Chip label={row.status} size="small" sx={{ bgcolor: STATUS_COLORS[row.status]?.bg, color: STATUS_COLORS[row.status]?.color, fontWeight: 700 }} />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => handleEdit(row)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(row.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* Potential Fulfillment Hint */}
                          {expandedRow !== row.id && row.balance_cartons > 0 && potentialMsg && (
                            <TableRow>
                              <TableCell colSpan={13} sx={{ py: 0.5, bgcolor: potentialMsg.type === 'success' ? '#e8f5e9' : potentialMsg.type === 'info' ? '#e3f2fd' : potentialMsg.type === 'warning' ? '#fff8e1' : '#ffebee', borderBottom: '1px solid #e0e0e0' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, cursor: 'pointer' }} onClick={() => toggleRowExpansion(row)}>
                                  <InfoIcon sx={{ fontSize: 18, color: potentialMsg.type === 'success' ? '#2e7d32' : potentialMsg.type === 'info' ? '#1565c0' : potentialMsg.type === 'warning' ? '#f57c00' : '#d32f2f' }} />
                                  <Typography variant="body2" fontWeight={600} sx={{ color: potentialMsg.type === 'success' ? '#2e7d32' : potentialMsg.type === 'info' ? '#1565c0' : potentialMsg.type === 'warning' ? '#e65100' : '#c62828' }}>
                                    {potentialMsg.text}
                                  </Typography>
                                  <Typography variant="caption" sx={{ ml: 'auto', color: '#666' }}>Click to expand ▼</Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}

                          {/* Expanded Details */}
                          <TableRow>
                            <TableCell colSpan={13} sx={{ py: 0, bgcolor: '#fafafa' }}>
                              <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
                                <Box sx={{ py: 3, px: 3 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                    <InventoryIcon sx={{ color: '#1565c0', fontSize: 28 }} />
                                    <Typography variant="h6" fontWeight={700} color="primary">Inventory Matching</Typography>
                                    <Chip label={`${row.product} | ${row.grade} | ${row.pack_size} | ${row.trace_id}`} sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }} />
                                  </Box>

                                  {matchLoading[row.id] ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={32} /></Box>
                                  ) : matchData[row.id] ? (
                                    <>
                                      <Grid container spacing={2} sx={{ mb: 3 }}>
                                        {[
                                          { label: 'Exact Match', value: matchData[row.id].totals?.exact_match_cartons || 0, bg: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)', icon: '✅' },
                                          { label: 'FCL Excess', value: matchData[row.id].totals?.fcl_excess_exact_cartons || 0, bg: 'linear-gradient(135deg, #ff9800 0%, #e65100 100%)', icon: '📦' },
                                          { label: 'Repack', value: matchData[row.id].totals?.fcl_excess_repack_cartons || 0, bg: 'linear-gradient(135deg, #e91e63 0%, #c2185b 100%)', icon: '🔄' },
                                          { label: 'Bulk/PBB', value: matchData[row.id].totals?.bulk_pbb_cartons || 0, bg: 'linear-gradient(135deg, #2196f3 0%, #1565c0 100%)', icon: '📋' },
                                          { label: 'Balance', value: matchData[row.id].totals?.balance || 0, bg: matchData[row.id].totals?.balance > 0 ? 'linear-gradient(135deg, #f44336 0%, #c62828 100%)' : 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)', icon: matchData[row.id].totals?.balance > 0 ? '⚠️' : '✅' },
                                        ].map((item, idx) => (
                                          <Grid item xs={6} sm={2.4} key={idx}>
                                            <Paper sx={{ p: 2, textAlign: 'center', background: item.bg, color: 'white', borderRadius: 2 }}>
                                              <Typography variant="h4" fontWeight={800}>{item.icon} {item.value}</Typography>
                                              <Typography variant="body2" fontWeight={600}>{item.label}</Typography>
                                            </Paper>
                                          </Grid>
                                        ))}
                                      </Grid>

                                      <Accordion defaultExpanded={matchData[row.id].exact_match?.length > 0}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#e8f5e9' }}>
                                          <CheckIcon sx={{ color: '#2e7d32', mr: 1 }} />
                                          <Typography fontWeight={700}>✅ Exact Match - {matchData[row.id].exact_match?.length || 0} pallets | {matchData[row.id].totals?.exact_match_cartons || 0} cartons</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                          <MatchTable data={matchData[row.id].exact_match || []} requirement={row} category="Exact_Match" />
                                        </AccordionDetails>
                                      </Accordion>

                                      <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#fff3e0' }}>
                                          <WarningIcon sx={{ color: '#e65100', mr: 1 }} />
                                          <Typography fontWeight={700}>📦 FCL Excess (Same Specs) - {matchData[row.id].fcl_excess_exact?.length || 0} pallets | {matchData[row.id].totals?.fcl_excess_exact_cartons || 0} cartons</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                          <MatchTable data={matchData[row.id].fcl_excess_exact || []} highlight requirement={row} category="FCL_Excess_Same_Specs" />
                                        </AccordionDetails>
                                      </Accordion>

                                      <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#fce4ec' }}>
                                          <TrendingIcon sx={{ color: '#c2185b', mr: 1 }} />
                                          <Typography fontWeight={700}>🔄 FCL Excess (Repack) - {matchData[row.id].fcl_excess_repack?.length || 0} pallets | {matchData[row.id].totals?.fcl_excess_repack_cartons || 0} cartons</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                          <MatchTable data={matchData[row.id].fcl_excess_repack || []} highlight requirement={row} category="FCL_Excess_Repack" />
                                        </AccordionDetails>
                                      </Accordion>

                                      <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#e3f2fd' }}>
                                          <InventoryIcon sx={{ color: '#1565c0', mr: 1 }} />
                                          <Typography fontWeight={700}>📋 Bulk/PBB - {matchData[row.id].bulk_pbb?.length || 0} pallets | {matchData[row.id].totals?.bulk_pbb_cartons || 0} cartons</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                          <MatchTable data={matchData[row.id].bulk_pbb || []} highlight requirement={row} category="Bulk_PBB" />
                                        </AccordionDetails>
                                      </Accordion>
                                    </>
                                  ) : null}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Paper>
        ))
      )}

      {/* Add Modal */}
      <Dialog open={showAddModal} onClose={() => setShowAddModal(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', color: 'white', fontWeight: 700 }}>
          <BulkAddIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Add Shipment Requirements
        </DialogTitle>
        <DialogContent sx={{ mt: 2, p: 3 }}>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          
          {existingFclList.length > 0 && !fclData.fcl_month && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#e3f2fd', borderRadius: 2 }}>
              <FormControlLabel control={<Switch checked={useExistingFcl} onChange={(e) => setUseExistingFcl(e.target.checked)} />} label={<Typography fontWeight={600}>Add to Existing FCL</Typography>} />
              {useExistingFcl && (
                <Autocomplete options={existingFclList} getOptionLabel={(opt) => `${opt.fcl_month} #${opt.fcl_no}${opt.po_number ? ` - PO: ${opt.po_number}` : ''}`}
                  onChange={(e, v) => handleExistingFclSelect(v)} renderInput={(params) => <TextField {...params} label="Select Existing FCL" sx={{ mt: 2 }} />} />
              )}
            </Paper>
          )}
          
          <Typography variant="subtitle1" color="primary" fontWeight={700} sx={{ mb: 2 }}>📦 FCL Information</Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <TextField select fullWidth required label="FCL Month" value={fclData.fcl_month} onChange={(e) => setFclData({ ...fclData, fcl_month: e.target.value })} disabled={useExistingFcl && selectedExistingFcl}>
                {FCL_MONTHS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth required label="FCL No" value={fclData.fcl_no} onChange={(e) => setFclData({ ...fclData, fcl_no: e.target.value })} disabled={useExistingFcl && selectedExistingFcl} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="PO Number" value={fclData.po_number} onChange={(e) => setFclData({ ...fclData, po_number: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Destination" value={fclData.destination} onChange={(e) => setFclData({ ...fclData, destination: e.target.value })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" color="primary" fontWeight={700}>🏷️ Product Lines</Typography>
            <Button startIcon={<AddIcon />} onClick={addProductRow} variant="outlined" size="small">Add Row</Button>
          </Box>

          {productRows.map((row, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: index % 2 === 0 ? '#fafafa' : '#fff', border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} color="primary">#{index + 1}</Typography>
                <Box>
                  <Tooltip title="Duplicate (same product, new Trace ID)"><IconButton size="small" color="primary" onClick={() => duplicateProductRow(index)}><CopyIcon fontSize="small" /></IconButton></Tooltip>
                  {productRows.length > 1 && <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => removeProductRow(index)}><RemoveIcon fontSize="small" /></IconButton></Tooltip>}
                </Box>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={1.5}>
                  <Autocomplete freeSolo size="small" options={filterOptions.products} value={row.product}
                    onChange={(e, v) => updateProductRow(index, 'product', v || '')}
                    onInputChange={(e, v, r) => { if (r === 'input') updateProductRow(index, 'product', v || ''); }}
                    renderInput={(params) => <TextField {...params} label="Product" required />} />
                </Grid>
                <Grid item xs={6} sm={4} md={1.5}>
                  <Autocomplete freeSolo size="small" options={filterOptions.grades} value={row.grade}
                    onChange={(e, v) => updateProductRow(index, 'grade', v || '')}
                    onInputChange={(e, v, r) => { if (r === 'input') updateProductRow(index, 'grade', v || ''); }}
                    renderInput={(params) => <TextField {...params} label="Grade" required />} />
                </Grid>
                <Grid item xs={6} sm={4} md={1.5}>
                  <Autocomplete freeSolo size="small" options={filterOptions.pack_sizes} value={row.pack_size}
                    onChange={(e, v) => updateProductRow(index, 'pack_size', v || '')}
                    onInputChange={(e, v, r) => { if (r === 'input') updateProductRow(index, 'pack_size', v || ''); }}
                    renderInput={(params) => <TextField {...params} label="Pack Size" required />} />
                </Grid>
                <Grid item xs={6} sm={4} md={1.5}>
                  <Autocomplete freeSolo size="small" options={filterOptions.brands} value={row.brand}
                    onChange={(e, v) => updateProductRow(index, 'brand', v || '')}
                    onInputChange={(e, v, r) => { if (r === 'input') updateProductRow(index, 'brand', v || ''); }}
                    renderInput={(params) => <TextField {...params} label="Brand" required />} />
                </Grid>
                <Grid item xs={6} sm={4} md={1.5}>
                  <TextField fullWidth size="small" label="Trace ID" value={row.trace_id} onChange={(e) => updateProductRow(index, 'trace_id', e.target.value)} required />
                </Grid>
                <Grid item xs={6} sm={4} md={1.5}>
                  <TextField fullWidth size="small" label="Day Code" value={row.day_code} onChange={(e) => updateProductRow(index, 'day_code', e.target.value)} required />
                </Grid>
                <Grid item xs={6} sm={4} md={1.5}>
                  <TextField fullWidth size="small" type="number" label="Cartons" value={row.planned_cartons} onChange={(e) => updateProductRow(index, 'planned_cartons', e.target.value)} required />
                </Grid>
                <Grid item xs={6} sm={4} md={1.5}>
                  <Paper sx={{ p: 1, bgcolor: '#e8f5e9', textAlign: 'center', borderRadius: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant="caption">LBs</Typography>
                    <Typography fontWeight={700} color="success.main">{calculateLbs(row.pack_size, row.planned_cartons).lbs.toLocaleString()}</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Paper sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              📊 Total: {productRows.filter(r => r.product && r.planned_cartons).length} lines | {productRows.reduce((sum, r) => sum + (parseInt(r.planned_cartons) || 0), 0).toLocaleString()} cartons | {productRows.reduce((sum, r) => sum + calculateLbs(r.pack_size, r.planned_cartons).lbs, 0).toLocaleString()} LBs
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Button onClick={() => { setShowAddModal(false); resetForm(); }} variant="outlined">Cancel</Button>
          <Button onClick={handleSubmitAll} variant="contained" disabled={formLoading || !fclData.fcl_month || !fclData.fcl_no}
            sx={{ background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)' }}>
            {formLoading ? <CircularProgress size={24} color="inherit" /> : `Add ${productRows.filter(r => r.product && r.planned_cartons).length} Line(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #e65100 100%)', color: 'white', fontWeight: 700 }}>
          <EditIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Edit Requirement
        </DialogTitle>
        <DialogContent sx={{ mt: 2, p: 3 }}>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={4}><TextField select fullWidth label="FCL Month" value={editFormData.fcl_month} onChange={(e) => setEditFormData({ ...editFormData, fcl_month: e.target.value })}>{FCL_MONTHS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}</TextField></Grid>
            <Grid item xs={4}><TextField fullWidth label="FCL No" value={editFormData.fcl_no} onChange={(e) => setEditFormData({ ...editFormData, fcl_no: e.target.value })} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="PO Number" value={editFormData.po_number} onChange={(e) => setEditFormData({ ...editFormData, po_number: e.target.value })} /></Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={3}><Autocomplete freeSolo options={filterOptions.products} value={editFormData.product} onChange={(e, v) => setEditFormData({ ...editFormData, product: v || '' })} onInputChange={(e, v, r) => { if (r === 'input') setEditFormData(p => ({ ...p, product: v || '' })); }} renderInput={(params) => <TextField {...params} label="Product" />} /></Grid>
            <Grid item xs={3}><Autocomplete freeSolo options={filterOptions.grades} value={editFormData.grade} onChange={(e, v) => setEditFormData({ ...editFormData, grade: v || '' })} onInputChange={(e, v, r) => { if (r === 'input') setEditFormData(p => ({ ...p, grade: v || '' })); }} renderInput={(params) => <TextField {...params} label="Grade" />} /></Grid>
            <Grid item xs={3}><Autocomplete freeSolo options={filterOptions.pack_sizes} value={editFormData.pack_size} onChange={(e, v) => setEditFormData({ ...editFormData, pack_size: v || '' })} onInputChange={(e, v, r) => { if (r === 'input') setEditFormData(p => ({ ...p, pack_size: v || '' })); }} renderInput={(params) => <TextField {...params} label="Pack Size" />} /></Grid>
            <Grid item xs={3}><Autocomplete freeSolo options={filterOptions.brands} value={editFormData.brand} onChange={(e, v) => setEditFormData({ ...editFormData, brand: v || '' })} onInputChange={(e, v, r) => { if (r === 'input') setEditFormData(p => ({ ...p, brand: v || '' })); }} renderInput={(params) => <TextField {...params} label="Brand" />} /></Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={3}><TextField fullWidth label="Trace ID" value={editFormData.trace_id} onChange={(e) => setEditFormData({ ...editFormData, trace_id: e.target.value })} /></Grid>
            <Grid item xs={3}><TextField fullWidth label="Day Code" value={editFormData.day_code} onChange={(e) => setEditFormData({ ...editFormData, day_code: e.target.value })} /></Grid>
            <Grid item xs={3}><TextField fullWidth type="number" label="Cartons" value={editFormData.planned_cartons} onChange={(e) => setEditFormData({ ...editFormData, planned_cartons: e.target.value })} /></Grid>
            <Grid item xs={3}><TextField select fullWidth label="Status" value={editFormData.status} onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}><MenuItem value="PENDING">Pending</MenuItem><MenuItem value="PARTIAL">Partial</MenuItem><MenuItem value="COMPLETED">Completed</MenuItem><MenuItem value="CANCELLED">Cancelled</MenuItem></TextField></Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="Destination" value={editFormData.destination} onChange={(e) => setEditFormData({ ...editFormData, destination: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Notes" value={editFormData.notes} onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Button onClick={() => { setShowEditModal(false); setEditingId(null); }} variant="outlined">Cancel</Button>
          <Button onClick={handleUpdate} variant="contained" disabled={formLoading} sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #e65100 100%)' }}>
            {formLoading ? <CircularProgress size={24} color="inherit" /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const MatchTable = ({ data = [], highlight, requirement, category }) => {
  
  // Safety check - if no data, show message
  if (!data || data.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No inventory found for this category
      </Alert>
    );
  }

  // Export to Excel
  const handleExportExcel = () => {
    const headers = ['Pallet', 'Product', 'Grade', 'Pack Size', 'Brand', 'Trace ID', 'Day Code', 'Bin Location', 'Cartons', 'Status'];
    const rows = data.map(row => [
      row.pallet_id || '-',
      row.product,
      row.grade,
      row.pack_size,
      row.brand,
      row.trace_id,
      row.day_code,
      row.bin_location || '-',
      row.cartons,
      row.status
    ]);
    
    let csv = headers.join(',') + '\n';
    rows.forEach(row => {
      csv += row.map(cell => `"${cell}"`).join(',') + '\n';
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${category || 'inventory'}_${requirement?.product || 'export'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print as PDF
  const handlePrint = () => {
    const totalCartons = data.reduce((sum, row) => sum + parseInt(row.cartons || 0), 0);
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Shipment Planning - ${category}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #1565c0; padding-bottom: 15px; }
          .header h1 { color: #1565c0; margin: 0; font-size: 24px; }
          .header h2 { color: #333; margin: 5px 0; font-size: 18px; }
          .header p { color: #666; margin: 5px 0; font-size: 12px; }
          .info-box { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .info-box table { width: 100%; border-collapse: collapse; }
          .info-box td { padding: 5px 10px; font-size: 13px; }
          .info-box td:first-child { font-weight: bold; color: #555; width: 120px; }
          .summary { display: flex; gap: 20px; margin-bottom: 20px; }
          .summary-card { background: #e3f2fd; padding: 15px 20px; border-radius: 8px; text-align: center; flex: 1; }
          .summary-card h3 { margin: 0; font-size: 24px; color: #1565c0; }
          .summary-card p { margin: 5px 0 0; font-size: 12px; color: #666; }
          table.data { width: 100%; border-collapse: collapse; font-size: 11px; }
          table.data th { background: #1565c0; color: white; padding: 10px 8px; text-align: left; font-weight: 600; }
          table.data td { padding: 8px; border-bottom: 1px solid #e0e0e0; }
          table.data tr:nth-child(even) { background: #fafafa; }
          table.data tr:hover { background: #f0f7ff; }
          .status { padding: 3px 8px; border-radius: 12px; font-size: 10px; font-weight: 600; }
          .status-produced { background: #e8f5e9; color: #2e7d32; }
          .status-repack { background: #e3f2fd; color: #1565c0; }
          .status-excess { background: #fff3e0; color: #e65100; }
          .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #999; border-top: 1px solid #e0e0e0; padding-top: 10px; }
          .highlight { background: #fff8e1 !important; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>CHOICE TRADING CORPORATION</h1>
          <h2>Shipment Planning - ${category}</h2>
          <p>Generated on ${new Date().toLocaleString()}</p>
        </div>
        
        ${requirement ? `
        <div class="info-box">
          <table>
            <tr><td>FCL:</td><td>${requirement.fcl_month} #${requirement.fcl_no}</td><td>Product:</td><td>${requirement.product}</td></tr>
            <tr><td>Grade:</td><td>${requirement.grade}</td><td>Pack Size:</td><td>${requirement.pack_size}</td></tr>
            <tr><td>Brand:</td><td>${requirement.brand}</td><td>Trace ID:</td><td>${requirement.trace_id}</td></tr>
            <tr><td>Day Code:</td><td>${requirement.day_code}</td><td>Planned:</td><td><strong>${requirement.planned_cartons} cartons</strong></td></tr>
          </table>
        </div>
        ` : ''}
        
        <div class="summary">
          <div class="summary-card">
            <h3>${data.length}</h3>
            <p>Pallets</p>
          </div>
          <div class="summary-card">
            <h3>${totalCartons.toLocaleString()}</h3>
            <p>Total Cartons</p>
          </div>
        </div>
        
        <table class="data">
          <thead>
            <tr>
              <th>Pallet</th>
              <th>Product</th>
              <th>Grade</th>
              <th>Pack Size</th>
              <th>Brand</th>
              <th>Trace ID</th>
              <th>Day Code</th>
              <th>Bin Location</th>
              <th>Cartons</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${data.map(row => `
              <tr>
                <td><strong>${row.pallet_id || '-'}</strong></td>
                <td>${row.product}</td>
                <td>${row.grade}</td>
                <td class="${highlight ? 'highlight' : ''}">${row.pack_size}</td>
                <td class="${highlight ? 'highlight' : ''}">${row.brand}</td>
                <td style="color: #7b1fa2;">${row.trace_id}</td>
                <td>${row.day_code}</td>
                <td><strong>${row.bin_location || '-'}</strong></td>
                <td><strong style="color: #d32f2f;">${row.cartons}</strong></td>
                <td><span class="status ${row.status === 'PRODUCED' ? 'status-produced' : row.status === 'REPACK-IN' ? 'status-repack' : 'status-excess'}">${row.status}</span></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          <p>Choice Trading Corporation - Cold Store Inventory Management System</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Button size="small" variant="outlined" startIcon={<span>📊</span>} onClick={handleExportExcel} sx={{ fontSize: '0.75rem' }}>
          Export Excel
        </Button>
        <Button size="small" variant="outlined" startIcon={<span>🖨️</span>} onClick={handlePrint} sx={{ fontSize: '0.75rem' }}>
          Print PDF
        </Button>
      </Box>
      <TableContainer sx={{ maxHeight: 350 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['Pallet', 'Product', 'Grade', 'Pack', 'Brand', 'Trace ID', 'Day Code', 'Bin Location', 'Cartons', 'Status'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#1565c0', color: 'white', fontSize: '0.85rem' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, idx) => (
              <TableRow key={idx} hover>
                <TableCell sx={{ fontWeight: 700, color: '#1565c0' }}>{row.pallet_id || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{row.product}</TableCell>
                <TableCell>{row.grade}</TableCell>
                <TableCell sx={{ bgcolor: highlight ? '#fff8e1' : 'inherit' }}>{row.pack_size}</TableCell>
                <TableCell sx={{ bgcolor: highlight ? '#fff8e1' : 'inherit' }}>{row.brand}</TableCell>
                <TableCell sx={{ color: '#7b1fa2' }}>{row.trace_id}</TableCell>
                <TableCell>{row.day_code}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>{row.bin_location || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#d32f2f', fontSize: '1rem' }}>{row.cartons}</TableCell>
                <TableCell><Chip label={row.status} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: row.status === 'PRODUCED' ? '#e8f5e9' : row.status === 'REPACK-IN' ? '#e3f2fd' : '#fff3e0', color: row.status === 'PRODUCED' ? '#2e7d32' : row.status === 'REPACK-IN' ? '#1565c0' : '#e65100' }} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ShipmentPlanningDashboard;